import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { socketManager } from 'utils/socketManager';
import {
  socketConnecting,
  socketConnected,
  socketDisconnected,
  socketError,
  socketReconnectAttempt,
} from 'store/slices/activitySlice';
import { Activity, ActivityStats } from 'types/interfaces/activity';
import {
  prependActivityToCache,
  prependActivityToFeedCache,
  updateActivityStatsCache,
} from 'store/services/activity/activityApi';

interface UseActivitySocketOptions {
  enabled?: boolean;
  onActivity?: (activity: Activity) => void;
  /**
   * Fired when a signal relevant to the computed Affordable feed arrives
   * (want added/deleted, or a trader's point balance changed). Carries no
   * payload — the consumer re-pulls the feed in response (debounced). The
   * Affordable feed is a server-computed snapshot, so we refetch rather than
   * patch the cache the way the event-type filters do.
   */
  onAvailableSignal?: () => void;
}

interface UseActivitySocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
}

/**
 * Hook to manage Socket.IO connection for activity feed.
 * Uses shared socket manager singleton.
 */
export const useActivitySocket = (
  options: UseActivitySocketOptions = {}
): UseActivitySocketReturn => {
  const { enabled = true, onActivity, onAvailableSignal } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const { isConnected, isConnecting, connectionError } = useSelector(
    (state: RootState) => state.activity
  );

  // Stable callback ref to avoid effect re-runs
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  // Stable ref for the Affordable-feed signal callback (same pattern as onActivity)
  const onAvailableSignalRef = useRef(onAvailableSignal);
  useEffect(() => {
    onAvailableSignalRef.current = onAvailableSignal;
  }, [onAvailableSignal]);

  // Handle incoming activity events
  const handleActivityEvent = useCallback(
    (data: { activity: Activity }) => {
      if (data?.activity) {
        // Update RTK Query caches with new activity
        prependActivityToCache(dispatch, data.activity);
        prependActivityToFeedCache(dispatch, data.activity);

        // Call optional callback
        if (onActivityRef.current) {
          onActivityRef.current(data.activity);
        }
      }
    },
    [dispatch]
  );

  // Handle incoming stats update events
  const handleStatsUpdate = useCallback(
    (data: ActivityStats) => {
      if (data) {
        updateActivityStatsCache(dispatch, data);
      }
    },
    [dispatch]
  );

  // Signal that the computed Affordable feed may have changed. Payload is
  // trigger-only (want_added: {activity}, want_deleted: {wantEntryId,userId},
  // balance_changed: {userId}) — we ignore it and let the consumer refetch.
  const handleAvailableSignal = useCallback((data?: unknown) => {
    // TEMP[AffordableDebug] — remove after real-time diagnosis
    console.log('[AffordableDebug] WS signal reached client:', data);
    onAvailableSignalRef.current?.();
  }, []);

  // Connection state handler
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        dispatch(socketConnected());
      } else {
        dispatch(socketDisconnected());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(socketConnecting());
    hasAcquiredRef.current = true;

    const socket = socketManager.acquireSocket();

    // Subscribe to connection state changes
    socketManager.addConnectionListener(handleConnectionChange);

    // Check current connection state
    if (socket.connected) {
      dispatch(socketConnected());
    }

    // Subscribe to socket events directly for immediate state updates
    const onConnect = () => {
      console.log('[ActivitySocket] Connected');
      dispatch(socketConnected());
    };

    const onDisconnect = (reason: string) => {
      console.log('[ActivitySocket] Disconnected:', reason);
      dispatch(socketDisconnected());
    };

    const onConnectError = (error: Error) => {
      console.error('[ActivitySocket] Connection error:', error.message);
      dispatch(socketError(error.message));
    };

    const onReconnectAttempt = (attemptNumber: number) => {
      console.log('[ActivitySocket] Reconnect attempt:', attemptNumber);
      dispatch(socketReconnectAttempt());
    };

    const onReconnectFailed = () => {
      console.error('[ActivitySocket] Reconnection failed after max attempts');
      dispatch(socketError('Reconnection failed'));
    };

    // Register connection event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);

    // Activity event handlers
    socket.on('activity:trade_sent', handleActivityEvent);
    socket.on('activity:trade_received', handleActivityEvent);
    socket.on('activity:want_added', handleActivityEvent);
    socket.on('activity:card_added', handleActivityEvent);
    socket.on('activity:trade_shipped', handleActivityEvent);
    socket.on('activity:stats_update', handleStatsUpdate);

    // Affordable-feed triggers — want_added also drives the event-type feed
    // above (handleActivityEvent); here it additionally signals a refetch of
    // the computed Affordable feed. want_deleted and balance_changed exist only
    // to drive that refetch.
    socket.on('activity:want_added', handleAvailableSignal);
    socket.on('activity:want_deleted', handleAvailableSignal);
    socket.on('activity:balance_changed', handleAvailableSignal);

    // Cleanup on unmount
    return () => {
      console.log('[ActivitySocket] Cleaning up');
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('activity:trade_sent', handleActivityEvent);
      socket.off('activity:trade_received', handleActivityEvent);
      socket.off('activity:want_added', handleActivityEvent);
      socket.off('activity:card_added', handleActivityEvent);
      socket.off('activity:trade_shipped', handleActivityEvent);
      socket.off('activity:stats_update', handleStatsUpdate);
      socket.off('activity:want_added', handleAvailableSignal);
      socket.off('activity:want_deleted', handleAvailableSignal);
      socket.off('activity:balance_changed', handleAvailableSignal);
      socketManager.removeConnectionListener(handleConnectionChange);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, dispatch, handleActivityEvent, handleStatsUpdate, handleAvailableSignal, handleConnectionChange]);

  return {
    isConnected,
    isConnecting,
    connectionError,
  };
};

export default useActivitySocket;
